import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { createApiClient } from '../api/client';
import { useAccessToken } from '../auth/useAuth';
import { actingFirmId, actingFirmQuery, useActingFirmId } from './advisorActingFirm';
import type { AdvisorSummaryResponse } from './types';

/** The React Query key for `GET /advisor/summary`. Exported so no consumer has to retype
 *  the string — `WorkspaceProvider`'s reset predicate reads it too. */
export const ADVISOR_SUMMARY_QUERY_KEY = ['advisor-summary'] as const;

/**
 * Matches `useAdvisorClients.ts` and `useAdvisorFirm.ts`, for the reason their comments
 * give: this answer moves once a day at most (a client's snapshot is written by the
 * nightly EOD run), and React Query's 30s default plus `refetchOnWindowFocus` would
 * otherwise refire a server-side fan-out on every alt-tab back into the browser.
 */
const ADVISOR_SUMMARY_STALE_TIME_MS = 5 * 60_000;

/**
 * The firm Overview's one read (steward-app#738, docs/26 §2a): connected client net worth
 * by currency, the roster size, and each client's own latest figure.
 *
 * ⚠️ **Fetched by `AdvisorOverview` and by NOTHING mounted app-wide** (steward-app#661).
 * This is the second advisor read whose server-side cost scales with a firm's roster —
 * the façade answers it by reading each of the caller's own client workspaces. #637 folded
 * the FIRST one into `GET /advisor/firms`, which `Layout.tsx` fetches once per session for
 * every authenticated user to decide whether to render the `/advisor` nav, so an advisor's
 * nav gate quietly became N workspace reads. Keep this hook where it is: mounting it from
 * a layout, a provider, or a route guard would put that cost straight back, and this time
 * against a bigger read.
 *
 * **Gated on a token, and that gate is not authorization.** `enabled` is false without one,
 * so an unauthenticated moment makes no request rather than fetching a payload it cannot
 * use. The actual boundary is appcore's membership check on every workspace the façade
 * reads (`handlers/advisor.ts` invariant 3); this is only a cost gate.
 *
 * ⚠️ **This page shipped dark behind `advisorOverviewEnabled()` (steward-app#738) and was
 * turned on for every environment on 2026-09-11 — that flag and its GH environment
 * variable are gone from this codebase.** If you are reading an old comment elsewhere
 * that still describes a flag-off state, it is stale; this hook now runs for every
 * connected-client roster of every advisor firm.
 *
 * **The error surface is REAL here, and `useAdvisorClients` now agrees.** That hook used
 * to swallow everything, on the argument that its failure cost one caption. steward-app#1036
 * step 3b made its payload the Clients ROSTER, at which point the same swallow would have
 * rendered an advisor's whole book as "No clients have connected yet" — so it returns the
 * full query result too now, and this paragraph no longer contrasts the two. This one's
 * failure costs the page's whole reason to exist, and the one thing an aggregate must never
 * do is render as though it succeeded — an empty summary and a failed one look identical in
 * a payload with zeroes in it, so the query's own error state is what the page branches on.
 * Hence the FULL query result, which is now the shape both advisor payload hooks return.
 *
 * ⚠️ **An ERRORED query ignores `staleTime` entirely, so it takes three options to stop
 * one refiring — `retry: false` is not close to enough.** `Query.isStaleByTime` returns
 * `true` unconditionally when `state.data === undefined`, and a query that has never
 * succeeded has no data, so every "is it stale?" gate answers yes forever and each
 * refetch trigger has to be closed by name (`retryOnMount` for MOUNT,
 * `refetchOnWindowFocus` and `refetchOnReconnect` for the other two — the full argument is
 * in `useAdvisorClients.ts`'s header, caught in review of #661). It matters here for the
 * same reason it mattered there: the failure that makes this query fail is usually the
 * façade's fan-out degrading, which is exactly when repeating it on every focus and
 * reconnect is the worst available behaviour.
 *
 * What that costs, stated plainly: a failed fetch stays failed until the user retries or
 * reloads. The page offers an explicit Retry, which is the honest replacement for the
 * automatic ones.
 */
export function useAdvisorSummary(): UseQueryResult<AdvisorSummaryResponse> {
  const token = useAccessToken();
  // Subscribed so a change of acting firm re-keys and refetches — see `advisorActingFirm.ts`.
  useActingFirmId();
  return useQuery({
    // In the key as well as the URL (steward-app#983), and it matters most here: this
    // payload SUMS, so a cache entry shared across firms would caption one firm's tile with
    // the other firm's total. `queryKey[0]` stays `'advisor-summary'` for the reset keep-list.
    queryKey: [...ADVISOR_SUMMARY_QUERY_KEY, actingFirmId()],
    queryFn: ({ queryKey }) =>
      createApiClient(token!).get<AdvisorSummaryResponse>(`/advisor/summary${actingFirmQuery(queryKey[1])}`),
    enabled: !!token,
    staleTime: ADVISOR_SUMMARY_STALE_TIME_MS,
    // All three, and none is redundant — see the ⚠️ above for which trigger each closes.
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
